import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { MiniCart, SlideOverDialog } from "@bluebottlecoffee/design-system";
import { loadStripe } from "@stripe/stripe-js";
import {
  toCartProps,
  toCartTableProps,
  toFreeShippingBarProps,
  toGiftMessageFormProps,
  toLineItemsProps,
  toOrderSummaryProps,
  toRemovePromoAlertProps,
} from "../lib/transformers/cart";
import { CartBaseProps } from "../lib/sanity/shared";
import { Cart as CartSchema } from "../lib/sanity-schema";
import { useCart } from "../lib/chord/packages/web/src/hooks/actions/use-cart";
import { useAnalytics } from "../lib/chord/packages/web/src/hooks/actions/use-analytics";
import { toMiniCartProps } from "../lib/transformers/mini-cart";
import {
  cartVariants,
  discountTotal,
  getPromoLabels,
  getPromoWithCode,
  isFreeShipping,
  shouldRenderFreeShippingBar,
} from "../lib/utils";
import { toLinkProps } from "../lib/transformers/link";
import { parseCookie } from "../functions/lib/common";
import { ChordUseCartReturnType } from "../lib/chord/types";

export interface DialogData extends Pick<CartBaseProps, "lang" | "region"> {
  miniCartCopy: CartSchema;
}

function validate(data: CartSchema) {
  if (!data.dialog.heading) {
    throw Error("MiniCart must have a heading.");
  }
}

export const MiniCartDialog: FunctionComponent<DialogData> = ({
  miniCartCopy,
  ...defaultProps
}) => {
  const { lang, region } = defaultProps;
  const [promoSuccess, setPromoSuccess] = useState<boolean>(true);
  const [promoErrorMessage, setPromoErrorMessage] = useState<string>(
    miniCartCopy.orderSummary.promoCopy.errorMessage[lang],
  );
  const {
    cart,
    loadCart,
    removeFromCart,
    modifyQuantity,
    addPromoCode,
    removePromoCode,
    modifyCart,
    checkout,
  }: ChordUseCartReturnType = useCart();
  const { data } = cart;
  const { trackViewCart } = useAnalytics();

  const handleModifyQuantity = useCallback(
    (lineItemId, quantity) => modifyQuantity(lineItemId, Math.max(quantity, 1)),
    [modifyQuantity],
  );

  validate(miniCartCopy);

  useEffect(() => {
    loadCart().catch((error) => {
      console.error(error.message);
    });
  }, [loadCart]);

  const onAddPromoCodeSubmit = async (promoCode: string) => {
    try {
      const promoResponse = await addPromoCode(promoCode);
      if (promoResponse.successful) {
        setPromoSuccess(true);
      } else {
        setPromoSuccess(false);
      }
    } catch (error) {
      setPromoSuccess(false);
      setPromoErrorMessage(error.message);
      console.error(error);
    }
  };

  const handleRemovePromoCode = async (promoCode: string) => {
    removePromoCode(promoCode);
    setPromoSuccess(true);
  };

  const onRemoveGiftMessage = async () => {
    try {
      await modifyCart({
        gift_note_attributes: {
          from: "",
          recipient: "",
          note: "",
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onAddGiftMessage = async (
    to: string,
    from: string,
    giftMessage?: string,
  ) => {
    try {
      await modifyCart({
        gift_note_attributes: {
          from,
          recipient: to,
          note: giftMessage,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Add Iterable campaign and template ID to Order metadata if values
   * are stored in cookies and castable to number
   *
   * IDs previously set on the Order metadata are removed if the relevant
   * cookies either a) have expired since the last time the method was
   * invoked, or b) no longer evaluate to a number.
   *
   * @returns { Promise<void> }
   */
  async function addOrRemoveCampaignMetadata(): Promise<void> {
    const { iterableEmailCampaignId, iterableTemplateId } = parseCookie();

    try {
      // A metadata key is removed by setting its value to an empty string:
      // https://chord.stoplight.io/docs/chord-oms/e8bbbcd029277-update-order
      await modifyCart({
        metadata: {
          campaignId: +iterableEmailCampaignId ? +iterableEmailCampaignId : "",
          templateId: +iterableTemplateId ? +iterableTemplateId : "",
        },
      });
    } catch (error) {
      console.error(
        "Failed to modify Iterable campaign metadata on Order:",
        error,
      );
    }
  }

  const onCheckoutSubmit = async () => {
    await addOrRemoveCampaignMetadata();

    const cart = await checkout();
    const stripe = await loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK);
    stripe.redirectToCheckout({ sessionId: cart.checkoutSessionId });
  };

  const transformedGiftNote =
    data.giftNote?.from && data.giftNote.recipient
      ? {
          to: data.giftNote.recipient,
          from: data.giftNote.from,
          giftMessage: data.giftNote.note,
        }
      : null;

  return (
    <SlideOverDialog
      contentType="minicart"
      dismissText={miniCartCopy.dialog.dismissText[lang]}
      title={miniCartCopy.dialog.heading[lang]}
      onOpen={trackViewCart}
      secondDismissButton={!data.lineItems || data.lineItems?.length === 0}
      secondDismissText={
        toLinkProps(miniCartCopy.returnToShopLink, region, lang).text
      }
    >
      <MiniCart
        {...toMiniCartProps(miniCartCopy, region, lang)}
        cartProps={{
          ...toCartProps(miniCartCopy, region, lang),
          cartTableProps: {
            ...toCartTableProps(miniCartCopy, lang),
            removeFromCart,
            modifyQuantity: handleModifyQuantity,
            cartLineItems: cartVariants({
              data,
              dialect: { region, lang },
            }),
            cartView: "mini",
          },
          renderFreeShippingBar: shouldRenderFreeShippingBar(data),
          freeShippingBarProps: {
            ...toFreeShippingBarProps(miniCartCopy.freeShippingBar, lang),
            estimatedTotal: {
              amount: Number(data.itemTotal) * 100,
              currency: data.currency,
            },
            freeShipPromoCode: isFreeShipping(data),
          },
          orderSummaryProps: {
            ...toOrderSummaryProps(miniCartCopy.orderSummary, lang),
            bgColor: miniCartCopy.dialog.bgColor,
            cartView: "mini",
            lineItems: {
              ...toLineItemsProps(miniCartCopy.orderSummary.lineItems, lang),
              credit:
                data.totalApplicableStoreCredit !== "0.0"
                  ? data.displayTotalApplicableStoreCredit
                  : undefined,
              currency: data.currency,
              discountTotal: discountTotal(data),
              estimatedTotalCost: data.displayOrderTotalAfterStoreCredit
                ? data.displayOrderTotalAfterStoreCredit
                : undefined,
              freeShipping: isFreeShipping(data),
              giftMessage: transformedGiftNote,
              grossSubtotal: data.displayItemTotal
                ? data.displayItemTotal
                : miniCartCopy.orderSummary.lineItems.tbd[lang],
              promoLabels: getPromoLabels(data),
              shippingCost:
                data.shipTotal === "0.0" ? undefined : data.displayShipTotal,
              variantsTotal: data.totalQuantity
                ? data.totalQuantity
                : undefined,
            },
            giftMessageForm: {
              ...toGiftMessageFormProps(
                miniCartCopy.orderSummary.giftMessageCopy,
                lang,
              ),
              giftMessageProp: transformedGiftNote,
              removeGiftMessage: onRemoveGiftMessage,
              addGiftMessage: onAddGiftMessage,
            },
            promoForm: {
              error: !promoSuccess,
              promoCode: getPromoWithCode(data),
              promoCopy: {
                add: miniCartCopy.orderSummary.promoCopy.add[lang],
                apply: miniCartCopy.orderSummary.promoCopy.apply[lang],
                placeholder:
                  miniCartCopy.orderSummary.promoCopy.placeholder[lang],
                errorMessage: promoErrorMessage,
              },
              ...toRemovePromoAlertProps(
                miniCartCopy.orderSummary.promoCopy,
                lang,
              ),
              removePromoCode: handleRemovePromoCode,
              addPromoCode: onAddPromoCodeSubmit,
            },
            /* TODO
             * subsPresent needs to be connected to data.subscriptionInCart, however, that
             * introduces a bug where if subscriptionInCart is true, then the user removes the subscription,
             * the Checkout Button remains inactive and the guest cannot checkout. @jesdeck
             */
            subsPresent: false,
            ctaClick: onCheckoutSubmit,
          },
          productRecsProps: {
            addToCartClick: async (sku, quantity) => {},
            emptyCart: true,
            productRecs: [],
            productRecsCopy: miniCartCopy.productRecsCopy[lang],
            productRecShopCards: [],
          },
        }}
      />
    </SlideOverDialog>
  );
};
